import { useEffect, useRef, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Adds the `is-visible` class (paired with `.v2-reveal` in tokens.css) the
 * first time the element scrolls into view. One-shot — never re-hides.
 */
export function useReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The observed element is often a wrapper while the `.v2-reveal` class sits
    // on the element itself and/or its descendants — reveal all of them.
    const reveal = () => {
      if (el.classList.contains("v2-reveal")) el.classList.add("is-visible");
      el.querySelectorAll(".v2-reveal").forEach((n) => n.classList.add("is-visible"));
    };
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      reveal();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal();
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [options]);
  return ref;
}

/**
 * Counts up from 0 to `end` once the element enters the viewport.
 * Returns [ref, value].
 */
export function useCountUp(end, duration = 1600) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setValue(end);
      return;
    }
    let raf;
    let started = false;
    const animate = (startTime) => {
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(end * eased));
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            requestAnimationFrame((t) => animate(t));
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [end, duration]);
  return [ref, value];
}
